import type { Account } from "@aptos-labs/ts-sdk";
import type { AccountBalance } from "@ekidenfi/ts-sdk";
import { comparer, makeAutoObservable, reaction, runInAction, when } from "mobx";

import { ACTIVE_TRADING_ADDRESS_KEY } from "@constants";
import { getEkidenSdkClient } from "@src/api/sdk";
import { fetchWhitelistStatus } from "@src/api/whitelist";
import {
  connectAndSubscribe as connectPrivateSubscriptions,
  disconnect as disconnectPrivateSubscriptions,
} from "@src/api/ws/private-subscriptions";
import { isValidAddress } from "@utils/Address";
import { indexDB } from "@utils/indexDB";

import type { SubAccount, Wallet, WalletConnectionStep } from "@src/services";
import {
  authService,
  connectionFlowService,
  createAccountFromPrivateKey,
  storageService,
  subAccountService,
} from "@src/services";

import type { RootStore } from ".";
import { DIALOG_TYPE } from "./DialogStore";

export type { SubAccount, Wallet, WalletConnectionStep };

export class AccountStore {
  // ==================== STATE ====================
  authStep: WalletConnectionStep = "idle";
  wallet: Wallet | null = null;

  /** True after API explicitly rejected this wallet (whitelist enforced). */
  whitelistDenied = false;
  /** True while whitelist GET is in flight for the current connect. */
  whitelistCheckPending = false;

  private previousAccountAddress: string | null = null;
  private _portfolioSeeded = false;
  private whitelistAllowedSnapshot: string | null = null;
  private whitelistInflight: Promise<boolean> | null = null;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async initialize() {
    this.setupReactions();
  }

  // ==================== COMPUTED - DELEGATED ====================
  get rootAccountToken() {
    return this.rootStore.tokenStore.rootAccountToken;
  }

  get tokensLoaded() {
    return this.rootStore.tokenStore.tokensLoaded;
  }

  get isGeneratingRootToken() {
    return this.rootStore.tokenStore.isGeneratingRootToken;
  }

  get allSubAccounts() {
    return this.rootStore.subAccountsStore.allSubAccounts;
  }

  get subAccount() {
    return this.rootStore.subAccountsStore.subAccount;
  }

  get subAccountAddress() {
    return this.rootStore.subAccountsStore.subAccountAddress;
  }

  get activeTradingAccountAddress() {
    return this.rootStore.subAccountsStore.activeTradingAccountAddress;
  }

  get isLoadingSubAccount() {
    return this.rootStore.subAccountsStore.isLoadingSubAccount;
  }

  get subAccountsLoaded() {
    return this.rootStore.subAccountsStore.subAccountsLoaded;
  }

  // ==================== COMPUTED ====================
  private get walletAddress(): string | null {
    const address = this.wallet?.account?.address?.toString();
    return isValidAddress(address) ? address : null;
  }

  get fundingSubAccount(): SubAccount | null {
    return this.walletAddress ? this.rootStore.subAccountsStore.getFundingSubAccount(this.walletAddress) : null;
  }

  get tradingSubAccount(): SubAccount | null {
    return this.walletAddress ? this.rootStore.subAccountsStore.getTradingSubAccount(this.walletAddress) : null;
  }

  get tradingSubAccounts(): SubAccount[] {
    return this.walletAddress ? this.rootStore.subAccountsStore.getTradingSubAccounts(this.walletAddress) : [];
  }

  get nextTradingAccountNonce(): string {
    return this.walletAddress ? this.rootStore.subAccountsStore.getNextTradingAccountNonce(this.walletAddress) : "0";
  }

  get activeTradingAccount(): SubAccount | null {
    return this.walletAddress ? this.rootStore.subAccountsStore.getActiveTradingAccount(this.walletAddress) : null;
  }

  get portfolio(): AccountBalance | null {
    return this.rootStore.balanceStore.activeTradingAccountBalance;
  }

  get isPartiallyConnected() {
    return !!this.wallet?.connected && !this.subAccount;
  }

  get isFullyConnected() {
    return !!this.wallet?.connected && !!this.subAccount;
  }

  get isAuthenticated() {
    return this.isFullyConnected && !!this.rootAccountToken;
  }

  private get isOnboardingBlockedByWhitelist() {
    return this.whitelistDenied || this.whitelistCheckPending;
  }

  /**
   * Waits for whitelist check when the wallet is connected. Returns false if access is denied.
   * Network/parsing failures are treated as whitelist off (returns true).
   */
  ensureWhitelistOrDeny = async (): Promise<boolean> => {
    if (!this.wallet?.connected || !this.wallet.account) return true;

    const addr = this.wallet.account.address.toString();
    if (!addr || addr === "0x" || !isValidAddress(addr)) return true;

    if (this.whitelistDenied) return false;
    if (this.whitelistAllowedSnapshot === addr) return true;

    if (!this.whitelistInflight) {
      this.whitelistCheckPending = true;
      this.whitelistInflight = this.applyWhitelistGateResult(addr).finally(() => {
        runInAction(() => {
          this.whitelistCheckPending = false;
          this.whitelistInflight = null;
        });
      });
    }

    return this.whitelistInflight;
  };

  private applyWhitelistGateResult = async (addr: string): Promise<boolean> => {
    const status = await fetchWhitelistStatus(addr);

    const stillSameWallet = this.wallet?.connected && this.wallet.account?.address.toString() === addr;

    if (!stillSameWallet) {
      return true;
    }

    if (status === "denied") {
      runInAction(() => {
        this.whitelistDenied = true;
        this.whitelistAllowedSnapshot = null;
        this.rootStore.dialogStore.closeAll();
        this.rootStore.dialogStore.open(DIALOG_TYPE.WHITELIST_DENIED);
      });
      return false;
    }
    runInAction(() => {
      this.whitelistDenied = false;
      this.whitelistAllowedSnapshot = addr;
      this.rootStore.dialogStore.close(DIALOG_TYPE.WHITELIST_DENIED);
    });
    return true;
  };

  private clearWhitelistGateForAccountSwitch = () => {
    runInAction(() => {
      this.whitelistDenied = false;
      this.whitelistAllowedSnapshot = null;
      this.whitelistInflight = null;
      this.whitelistCheckPending = false;
      this.rootStore.dialogStore.close(DIALOG_TYPE.WHITELIST_DENIED);
    });
  };

  // ==================== SETTERS ====================
  setWallet = (wallet: Wallet | null) => {
    this.wallet = wallet;
    // Loading is handled by reactions (onAccountChange, onAptosFetch)
  };

  setSubAccount = (account: Account | null) => {
    this.rootStore.subAccountsStore.setSubAccount(account);
  };

  // ==================== TRADING ACCOUNT ====================
  setActiveTradingAccount = async (address: string) => {
    await this.rootStore.subAccountsStore.setActiveTradingAccountAddress(address);

    let tradingAccount = this.tradingSubAccounts.find((acc) => acc.address === address);
    if (!tradingAccount) return;

    if (!tradingAccount.privateKey && tradingAccount.orderIndex && this.wallet) {
      const restored = await this.rootStore.subAccountsStore.findAndRestoreTradingAccount(
        this.wallet,
        address,
        tradingAccount.orderIndex.toString(),
      );
      if (!restored) return;
      tradingAccount = restored;
    }

    this.setSubAccount(createAccountFromPrivateKey(tradingAccount.privateKey));
    await this.ensureTokensGenerated();
  };

  private ensureTokensGenerated = async () => {
    if (!this.rootAccountToken && this.wallet) {
      const isLinked = await this.checkAccountsLinkedToBlockchain();
      if (isLinked) {
        this.promptForAuth();
        return;
      }
    }

    if (
      this.activeTradingAccount &&
      this.rootAccountToken &&
      !this.rootStore.balanceStore.getAccountBalanceForUser(this.activeTradingAccount.address)
    ) {
      await this.rootStore.balanceStore.fetchAccountBalances().catch(() => {});
    }
  };

  // ==================== ACCOUNT SIGNING ====================
  signAccounts = async () => {
    if (!this.wallet) return;
    await this.rootStore.subAccountsStore.signAccounts(this.wallet);
  };

  finalizeAccountCreation = async () => {
    if (!this.wallet?.account) throw new Error("Missing wallet for account creation");
    if (this.whitelistDenied) return;

    await this.rootStore.subAccountsStore.finalizeAccountCreation(this.wallet);

    // If token is already valid, skip auth step and complete
    if (this.rootAccountToken && !authService.isTokenExpired(this.rootAccountToken)) {
      this.authStep = "done";

      if (
        !this.isOnboardingBlockedByWhitelist &&
        (await connectionFlowService.shouldShowStartTradingDialog(this.wallet))
      ) {
        this.rootStore.dialogStore.open(DIALOG_TYPE.START_TRADING);
      }
      this.rootStore.dialogStore.close(DIALOG_TYPE.TERMS);
      return;
    }

    // Otherwise transition to auth step — user needs to sign to authenticate
    this.authStep = "auth";
  };

  // ==================== BLOCKCHAIN OPERATIONS ====================
  checkAccountsLinkedToBlockchain = async (forceRefresh = false): Promise<boolean> => {
    if (!this.wallet?.account) return false;
    const onFailure = () => {
      this.rootStore.accountStore.showToast(
        "error",
        "Failed to check accounts linked to blockchain",
        "Please try again later. Seems like you was ratelimited",
      );
    };
    return this.rootStore.subAccountsStore.checkAccountsLinkedToBlockchain(
      this.wallet.account.address.toString(),
      forceRefresh,
      onFailure,
    );
  };

  linkAccountsToBlockchain = async () => {
    if (!this.wallet?.account) throw new Error("No wallet connected");

    if (await this.checkAccountsLinkedToBlockchain(true)) {
      this.showToast("info", "Already linked", "Your accounts are already linked to the blockchain");
      return;
    }

    try {
      await this.rootStore.subAccountsStore.linkAccountsToBlockchain(this.wallet);
      this.showToast("success", "Accounts linked", "Your accounts have been successfully linked to the blockchain");
    } catch (error) {
      this.handleBlockchainError(error, "Account Linking Failed", "Failed to link accounts");
      throw error;
    }
  };

  addTradingAccount = async () => {
    if (!this.wallet?.account) return;

    const address = this.wallet.account.address.toString();

    try {
      const newAccount = await this.rootStore.subAccountsStore.addTradingAccount(this.wallet);

      await this.loadSubAccount(address, true);
      this.showToast("success", "Trading Account Added", "New trading account created successfully!");
      return newAccount;
    } catch (error) {
      this.handleBlockchainError(error, "Account Creation Failed", "Failed to create trading account");
      throw error;
    }
  };

  private handleBlockchainError = (error: unknown, title: string, defaultMessage: string) => {
    const msg = error instanceof Error ? error.message : String(error);
    const isInsufficientFunds = /INSUFFICIENT_BALANCE|insufficient balance|insufficient funds|not enough gas/i.test(
      msg,
    );

    this.showToast(
      "error",
      isInsufficientFunds ? "Insufficient APT Balance" : title,
      isInsufficientFunds ? "You don't have enough APT to cover the gas fee." : `${defaultMessage}: ${msg}`,
    );
  };

  fetchSubAccountsFromAptos = async (rootAddress?: string): Promise<SubAccount[]> => {
    const address = rootAddress ?? this.wallet?.account?.address?.toString();
    return address ? this.rootStore.subAccountsStore.fetchSubAccountsFromAptos(address) : [];
  };

  generateTradingAccountByNonce = async (nonce: string): Promise<SubAccount | null> => {
    return this.wallet ? this.rootStore.subAccountsStore.generateTradingAccountByNonce(this.wallet, nonce) : null;
  };

  // ==================== TOKEN GENERATION (DELEGATION) ====================
  generateRootAccountToken = async (force = false) => {
    if (!this.wallet) return;
    await this.rootStore.tokenStore.generateRootAccountToken(this.wallet, force);
  };

  handleAuth = async () => {
    if (this.rootStore.geoFenceStore.isRestricted) {
      this.showGeoFenceRestrictionToast();
      return;
    }
    if (this.whitelistDenied) return;
    if (!this.wallet) return;
    await this.rootStore.tokenStore.generateRootAccountToken(this.wallet, true);
    this.authStep = "done";

    if (
      !this.isOnboardingBlockedByWhitelist &&
      (await connectionFlowService.shouldShowStartTradingDialog(this.wallet))
    ) {
      this.rootStore.dialogStore.open(DIALOG_TYPE.START_TRADING);
    }
    this.rootStore.dialogStore.close(DIALOG_TYPE.TERMS);
  };

  promptForAuth = () => {
    if (this.isOnboardingBlockedByWhitelist) return;
    if (this.rootStore.geoFenceStore.isRestricted) {
      this.showGeoFenceRestrictionToast();
      return;
    }
    // Don't prompt if tokens haven't loaded yet from cache
    // if (!this.tokensLoaded) return;
    // Don't prompt if token is already valid
    if (this.rootAccountToken && !authService.isTokenExpired(this.rootAccountToken)) return;
    // Don't prompt if accounts aren't fully set up (no private keys)
    if (!this.subAccount) return;
    if (this.rootStore.dialogStore.isVisible(DIALOG_TYPE.TERMS)) return;
    this.authStep = "auth";
    this.rootStore.dialogStore.open(DIALOG_TYPE.TERMS);
  };

  // ==================== CONNECTION FLOW ====================
  initConnectionFlow = () => {
    if (this.rootStore.geoFenceStore.isRestricted) {
      this.showGeoFenceRestrictionToast();
      this.rootStore.dialogStore.close(DIALOG_TYPE.SELECT_WALLET);
      return;
    }

    if (this.whitelistDenied) return;

    if (this.isAuthenticated) {
      this.authStep = "done";
      return;
    }

    // Wait for tokens to load before deciding if auth is needed
    if (this.isFullyConnected && !this.rootAccountToken) {
      this.promptForAuth();
      return;
    }

    if (this.isPartiallyConnected && !this.isLoadingSubAccount && this.subAccountsLoaded) {
      this.openTermsDialogWithDelay();
      return;
    }

    if (this.wallet?.connected && !this.subAccountsLoaded) return;

    this.authStep = "wallet";
    this.rootStore.dialogStore.open(DIALOG_TYPE.SELECT_WALLET);
  };

  onWalletConnected = async () => {
    if (this.rootStore.geoFenceStore.isRestricted) {
      this.showGeoFenceRestrictionToast();
      await this.disconnect();
      return;
    }

    const { dialogStore } = this.rootStore;
    if (dialogStore.isVisible(DIALOG_TYPE.SELECT_WALLET)) dialogStore.close(DIALOG_TYPE.SELECT_WALLET);

    const allowed = await this.ensureWhitelistOrDeny();
    if (!allowed) return;

    if (this.isFullyConnected) {
      dialogStore.closeAll();
      return;
    }

    // Wait briefly for session data, but don't block UI for too long
    await this.waitForSubAccountsLoaded();

    const address = this.wallet?.account?.address?.toString();
    if (address && this.rootStore.subAccountsStore.hasAccountsWithKeys(address)) return;

    if (!connectionFlowService.hasAcceptedTerms()) {
      this.openTermsDialogWithDelay();
    }
  };

  private waitForSubAccountsLoaded = () => {
    if (this.subAccountsLoaded) {
      return Promise.resolve();
    }
    return when(() => this.subAccountsLoaded, { timeout: 500 }).catch(() => {});
  };

  private waitForTokensLoaded = () => {
    if (this.tokensLoaded) return Promise.resolve();
    return when(() => this.tokensLoaded, { timeout: 500 }).catch(() => {});
  };

  private openTermsDialogWithDelay = (delay = 500) => {
    setTimeout(() => {
      if (this.isOnboardingBlockedByWhitelist) return;
      if (!this.isFullyConnected && !this.rootStore.dialogStore.isVisible(DIALOG_TYPE.TERMS)) {
        this.authStep = "terms";
        this.rootStore.dialogStore.open(DIALOG_TYPE.TERMS);
      }
    }, delay);
  };

  onTermsAccepted = async () => {
    if (this.rootStore.geoFenceStore.isRestricted) {
      this.showGeoFenceRestrictionToast();
      this.rootStore.dialogStore.close(DIALOG_TYPE.TERMS);
      return;
    }

    if (this.whitelistDenied) return;

    await this.createSubAccount();
    this.authStep = "done";

    if (
      !this.isOnboardingBlockedByWhitelist &&
      (await connectionFlowService.shouldShowStartTradingDialog(this.wallet))
    ) {
      this.rootStore.dialogStore.open(DIALOG_TYPE.START_TRADING);
    }
    this.rootStore.dialogStore.close(DIALOG_TYPE.TERMS);
  };

  onRejectConnection = () => void this.disconnect();

  disconnect = async (preserveData?: { vaultContractKey?: string }) => {
    await disconnectPrivateSubscriptions();

    await connectionFlowService.disconnect(
      this.wallet,
      { reset: this.reset, closeAllDialogs: () => this.rootStore.dialogStore.closeAll() },
      preserveData,
    );
  };

  shouldShowStartTradingDialog = async (forceRefresh = false) =>
    connectionFlowService.shouldShowStartTradingDialog(this.wallet, forceRefresh);

  updateNoncesFromBlockchain = async () => {
    const address = this.wallet?.account?.address.toString();
    if (address) {
      await this.rootStore.subAccountsStore.updateNoncesFromBlockchain(address);
    }
  };

  // ==================== REACTIONS ====================
  private setupReactions = () => {
    this.onAccountChange();
    this.onFullyConnected();
    this.onPortfolioSeed();
    this.onWebSocket();
    this.onTokenChange();
  };

  private onTokenChange = () => {
    reaction(
      () => this.rootAccountToken,
      (token) => {
        if (token) {
          getEkidenSdkClient().setToken(token);
        } else {
          getEkidenSdkClient().setToken("");
        }
      },
      { fireImmediately: true },
    );
  };

  private onAccountChange = () => {
    reaction(
      () => ({
        address: this.wallet?.account?.address?.toString() ?? null,
        loaded: this.subAccountsLoaded,
      }),
      ({ address }) => {
        if (address === "0x") return;

        const isSwitch = this.previousAccountAddress && address && this.previousAccountAddress !== address;

        if (address) {
          this.handleConnectedAccount(address, !!isSwitch);
        } else if (this.isFullyConnected) {
          this.handleFullyConnectedNoAccount();
        } else if (this.previousAccountAddress) {
          // Only reset on genuine disconnect (previously had an account, now don't).
          // Avoids resetting during intermediate wallet adapter states (loading/connecting)
          // which would incorrectly clear tokensLoaded before the wallet finishes connecting.
          this.reset();
        }

        this.previousAccountAddress = address || null;
      },
      { equals: comparer.structural, fireImmediately: true },
    );
  };

  private handleConnectedAccount = (address: string, isSwitch: boolean) => {
    if (isSwitch) {
      this.clearWhitelistGateForAccountSwitch();
      this.resetUserData();
      storageService.clear();
      subAccountService.clearCaches();
    }

    void (async () => {
      const ok = await this.ensureWhitelistOrDeny();
      if (!ok) return;
      if (!this.wallet?.connected) return;
      if (!this.subAccountsLoaded) return;

      const storedAccounts = this.allSubAccounts.get(address);
      if (storedAccounts?.length) {
        this.restoreAccountsFromStorage(address, storedAccounts).then(() => {
          if (!this.subAccount && this.wallet?.connected) {
            this.openTermsDialogWithDelay();
          }
        });
      } else {
        this.loadSubAccount(address).then(() => {
          if (
            !this.subAccount &&
            this.wallet?.connected &&
            !this.rootStore.subAccountsStore.hasAccountsWithKeys(address)
          ) {
            this.openTermsDialogWithDelay();
          }
        });
      }
    })();
  };

  private restoreAccountsFromStorage = async (_address: string, storedAccounts: SubAccount[]) => {
    const tradingWithKeys = storedAccounts.filter((acc) => acc.type === "trading" && acc.privateKey);
    if (tradingWithKeys.length === 0) {
      return;
    }

    try {
      // IMPORTANT: First check if accounts exist in blockchain before initializing
      // Don't set subAccount or generate token if accounts are not linked to blockchain
      const isLinked = await this.checkAccountsLinkedToBlockchain();
      if (!isLinked) {
        // Accounts exist in localStorage but not in blockchain - don't initialize
        // User needs to link accounts first
        return;
      }

      // Only initialize subAccount after confirming accounts are in blockchain
      this.rootStore.subAccountsStore.initializeFromStorage(storedAccounts);

      const savedAddress = await indexDB.get(ACTIVE_TRADING_ADDRESS_KEY);

      // Set active trading account if not already set
      if (!this.activeTradingAccountAddress) {
        const selected = this.rootStore.subAccountsStore.selectInitialTradingAccount(
          storedAccounts,
          savedAddress as string,
        );
        if (selected) {
          await this.rootStore.subAccountsStore.setActiveTradingAccountAddress(selected.address);
        }
      }

      // Wait for tokens to be loaded from session before checking
      if (!this.tokensLoaded) {
        await this.waitForTokensLoaded();
      }

      // If token is already valid, use it — otherwise prompt user to sign
      if (this.rootAccountToken && !authService.isTokenExpired(this.rootAccountToken)) {
        getEkidenSdkClient().setToken(this.rootAccountToken);
        await this.rootStore.balanceStore.fetchAccountBalances();
      } else {
        this.promptForAuth();
      }
    } catch (error) {
      console.error("Error restoring accounts from storage:", error);
    }
  };

  private handleFullyConnectedNoAccount = () => {
    if (this.isOnboardingBlockedByWhitelist) return;
    const networkName = this.wallet?.network?.name?.toLowerCase();
    if (networkName && !["testnet", "test"].includes(networkName)) {
      this.shouldShowStartTradingDialog().then((show) => {
        if (show && !this.isOnboardingBlockedByWhitelist) {
          this.rootStore.dialogStore.open(DIALOG_TYPE.START_TRADING);
        }
      });
    }
  };

  private onFullyConnected = () => {
    reaction(
      () => this.isFullyConnected,
      async (connected) => {
        if (!connected) return;

        // Wait for tokens to load before deciding if auth is needed
        if (!this.tokensLoaded) {
          await this.waitForTokensLoaded();
        }

        if (this.isOnboardingBlockedByWhitelist) return;

        if (!this.rootAccountToken) {
          const isLinked = await this.checkAccountsLinkedToBlockchain();
          if (isLinked) {
            this.promptForAuth();
          }
        }
        this.shouldShowStartTradingDialog().then((show) => {
          if (show && !this.isOnboardingBlockedByWhitelist) {
            this.rootStore.dialogStore.open(DIALOG_TYPE.START_TRADING);
          }
        });
      },
    );
  };

  private onPortfolioSeed = () => {
    reaction(
      () => [this.isFullyConnected, this.rootAccountToken] as const,
      ([connected, token]) => {
        if (!this._portfolioSeeded && connected && token) {
          this._portfolioSeeded = true;
          if (
            this.activeTradingAccount &&
            !this.rootStore.balanceStore.getAccountBalanceForUser(this.activeTradingAccount.address)
          ) {
            this.rootStore.balanceStore.fetchAccountBalances().catch(() => {});
          }
        }
      },
      { fireImmediately: true },
    );
  };

  private onWebSocket = () => {
    reaction(
      () => ({
        rootToken: this.rootAccountToken,
        // isSdkReady becomes true only after setConfigReady(true) +
        // resetEkidenSdkClient() have already been called in ConfigProvider,
        // guaranteeing that getEkidenSdkClient() returns a client with proper
        // WS URLs and the current token already set on it.
        sdkReady: this.rootStore.configStore.isSdkReady,
      }),
      async ({ rootToken, sdkReady }) => {
        if (!rootToken) {
          await disconnectPrivateSubscriptions();
          return;
        }
        if (!sdkReady) return;
        await connectPrivateSubscriptions(rootToken, {
          myOrdersStore: this.rootStore.myOrdersStore,
          accountStore: this,
          marketStore: this.rootStore.marketStore,
          balanceStore: this.rootStore.balanceStore,
          toastStore: this.rootStore.toastStore,
        });
      },
      { fireImmediately: true, equals: comparer.structural },
    );
  };

  // ==================== PRIVATE METHODS ====================
  private loadSubAccount = async (address: string, forceRefresh = false) => {
    await this.rootStore.subAccountsStore.loadSubAccount(
      address,
      {
        generateRootAccountToken: async () => {
          // Don't auto-sign — prompt user to sign via button instead
          const isLinked = await this.checkAccountsLinkedToBlockchain();
          if (!this.rootAccountToken) {
            await this.waitForTokensLoaded();
          }
          if (isLinked && !this.rootAccountToken) {
            this.promptForAuth();
          }
        },
        fetchAllPortfolios: () => this.rootStore.balanceStore.fetchAccountBalances(),
      },
      forceRefresh,
    );
  };

  private createSubAccount = async () => {
    if (!this.wallet?.account) return;

    await this.rootStore.subAccountsStore.createSubAccount(this.wallet, {
      showToast: this.rootStore.toastStore.show,
    });
  };

  private showToast = (type: "error" | "info" | "success" | "warning", title: string, content: string) => {
    this.rootStore.toastStore.show({ type, title, content });
  };

  private showGeoFenceRestrictionToast = () => {
    this.rootStore.toastStore.show({
      type: "warning",
      title: "Limited Functionality",
      content: "Unavailable in restricted jurisdiction.",
    });
  };

  private resetUserData = () => {
    this.rootStore.balanceStore.reset();
    this.rootStore.myOrdersStore.reset();
    this._portfolioSeeded = false;
  };

  reset = () => {
    this.resetUserData();
    this.wallet = null;
    this.rootStore.tokenStore.reset();
    this.rootStore.subAccountsStore.reset();
    this.authStep = "idle";
    this.previousAccountAddress = null;
    this.whitelistDenied = false;
    this.whitelistCheckPending = false;
    this.whitelistAllowedSnapshot = null;
    this.whitelistInflight = null;
  };
}
